import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Trash2 } from 'lucide-react';

/**
 * Shared confirmation dialog — replaces window.confirm.
 *
 * Optional secondaryConfirmLabel / onSecondaryConfirm adds a middle action
 * (e.g. "Save draft" beside "Publish").
 * Set compact to drop the large icon and tighten spacing.
 */
export default function ConfirmModal({
  open,
  title = 'Are you sure?',
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  secondaryConfirmLabel = null,
  variant = 'danger',
  compact = false,
  loading = false,
  onConfirm,
  onSecondaryConfirm = null,
  onCancel,
  requirePhrase = null,
  checkboxLabel = null,
  extraCheckboxLabel = null,
}) {
  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <ConfirmModalBody
      title={title}
      message={message}
      confirmLabel={confirmLabel}
      cancelLabel={cancelLabel}
      secondaryConfirmLabel={secondaryConfirmLabel}
      variant={variant}
      compact={compact}
      loading={loading}
      onConfirm={onConfirm}
      onSecondaryConfirm={onSecondaryConfirm}
      onCancel={onCancel}
      requirePhrase={requirePhrase}
      checkboxLabel={checkboxLabel}
      extraCheckboxLabel={extraCheckboxLabel}
    />,
    document.body,
  );
}

function ConfirmModalBody({
  title,
  message,
  confirmLabel,
  cancelLabel,
  secondaryConfirmLabel,
  variant,
  compact,
  loading,
  onConfirm,
  onSecondaryConfirm,
  onCancel,
  requirePhrase,
  checkboxLabel,
  extraCheckboxLabel,
}) {
  const [phrase, setPhrase] = useState('');
  const [checked, setChecked] = useState(false);
  const [extraChecked, setExtraChecked] = useState(false);

  const isDanger = variant === 'danger';
  const Icon = isDanger ? Trash2 : AlertTriangle;
  const phraseOk = !requirePhrase || phrase.trim() === String(requirePhrase);
  const checkOk = !checkboxLabel || checked;
  const extraOk = !extraCheckboxLabel || extraChecked;
  const canConfirm = phraseOk && checkOk && extraOk && !loading;
  const hasSecondary = Boolean(secondaryConfirmLabel && onSecondaryConfirm);

  return (
    <div
      className="fixed inset-0 z-[12000] flex items-center justify-center bg-[rgba(15,23,42,0.56)] p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="swotify-confirm-title"
    >
      <button
        type="button"
        className="absolute inset-0 cursor-default"
        aria-label="Dismiss"
        disabled={loading}
        onClick={loading ? undefined : onCancel}
      />
      <div
        className={`relative z-[1] w-full rounded-2xl border border-[var(--sw-surface-200,#E8E4D7)] bg-white text-center shadow-[0_18px_50px_rgba(2,6,23,0.28)] ${
          compact ? 'max-w-sm p-4' : 'max-w-md p-6'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {!compact ? (
          <div
            className={`mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full ${
              isDanger
                ? 'bg-[var(--sw-coral-50,#FEF2F2)] text-[var(--sw-coral-700,#B91C1C)]'
                : 'bg-amber-50 text-amber-800'
            }`}
          >
            <Icon className="h-6 w-6" />
          </div>
        ) : null}
        <h3
          id="swotify-confirm-title"
          className={`font-semibold text-[var(--sw-text-primary,#0B1020)] ${
            compact ? 'text-base' : 'text-lg'
          }`}
        >
          {title}
        </h3>
        {message ? (
          <p
            className={`mt-1.5 whitespace-pre-line text-[var(--sw-text-secondary,#5A6079)] ${
              compact ? 'text-xs leading-snug' : 'mt-2 text-sm leading-relaxed'
            }`}
          >
            {message}
          </p>
        ) : null}
        {requirePhrase ? (
          <label className="mt-4 block text-left text-xs font-medium text-[var(--sw-text-secondary,#5A6079)]">
            Type {requirePhrase} to confirm
            <input
              type="text"
              value={phrase}
              onChange={(e) => setPhrase(e.target.value)}
              autoComplete="off"
              className="mt-1 w-full rounded-lg border border-[var(--sw-surface-200,#E8E4D7)] px-3 py-2 text-sm text-[var(--sw-text-primary,#0B1020)]"
              placeholder={requirePhrase}
            />
          </label>
        ) : null}
        {checkboxLabel ? (
          <label className="mt-3 flex items-start gap-2 text-left text-xs text-[var(--sw-text-secondary,#5A6079)]">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
            />
            <span>{checkboxLabel}</span>
          </label>
        ) : null}
        {extraCheckboxLabel ? (
          <label className="mt-2 flex items-start gap-2 text-left text-xs text-[var(--sw-text-secondary,#5A6079)]">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={extraChecked}
              onChange={(e) => setExtraChecked(e.target.checked)}
            />
            <span>{extraCheckboxLabel}</span>
          </label>
        ) : null}
        <div
          className={`flex gap-2 ${compact ? 'mt-4' : 'mt-6 gap-3'} ${
            hasSecondary || compact ? 'flex-col' : ''
          }`}
        >
          {hasSecondary ? (
            <>
              <button
                type="button"
                onClick={onConfirm}
                disabled={!canConfirm}
                className={`w-full rounded-xl px-4 text-sm font-semibold text-white transition disabled:opacity-60 ${
                  compact ? 'py-2.5' : 'py-2.5'
                } ${
                  isDanger
                    ? 'bg-[var(--sw-coral-600,#DC2626)] hover:bg-[var(--sw-coral-700,#B91C1C)]'
                    : 'bg-[var(--sw-sapphire-700,#1B3478)] hover:bg-[var(--sw-sapphire-800,#0A1733)]'
                }`}
              >
                {loading ? 'Please wait…' : confirmLabel}
              </button>
              <button
                type="button"
                onClick={onSecondaryConfirm}
                disabled={!canConfirm}
                className="w-full rounded-xl border border-[var(--sw-sapphire-200,#C5D0EF)] bg-[var(--sw-sapphire-50,#EEF2FB)] px-4 py-2.5 text-sm font-semibold text-[var(--sw-sapphire-800,#0A1733)] transition hover:bg-[var(--sw-sapphire-100,#DCE4F7)] disabled:opacity-60"
              >
                {loading ? 'Please wait…' : secondaryConfirmLabel}
              </button>
              <button
                type="button"
                onClick={onCancel}
                disabled={loading}
                className="w-full rounded-xl border border-[var(--sw-surface-200,#E8E4D7)] bg-white px-4 py-2 text-sm font-medium text-[var(--sw-text-secondary,#5A6079)] transition hover:bg-[var(--sw-surface-50,#FBF8F1)] disabled:opacity-60"
              >
                {cancelLabel}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={onCancel}
                disabled={loading}
                className="flex-1 rounded-xl border border-[var(--sw-surface-200,#E8E4D7)] bg-white px-4 py-2.5 text-sm font-semibold text-[var(--sw-text-primary,#0B1020)] transition hover:bg-[var(--sw-surface-50,#FBF8F1)] disabled:opacity-60"
              >
                {cancelLabel}
              </button>
              <button
                type="button"
                onClick={onConfirm}
                disabled={!canConfirm}
                className={`flex-1 rounded-xl px-4 py-2.5 text-sm font-semibold text-white transition disabled:opacity-60 ${
                  isDanger
                    ? 'bg-[var(--sw-coral-600,#DC2626)] hover:bg-[var(--sw-coral-700,#B91C1C)]'
                    : 'bg-[var(--sw-sapphire-700,#1B3478)] hover:bg-[var(--sw-sapphire-800,#0A1733)]'
                }`}
              >
                {loading ? 'Please wait…' : confirmLabel}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
